import axios from 'axios';

// Autentifikacija ir CSRF apsauga:
//   X-Auth-Token  – sesijos žetonas (gaunamas prisijungus)
//   X-CSRF-Token  – CSRF žetonas (sesijos arba anoniminis, GET /auth/csrf)
// Backend'as atmeta visas POST/PUT/PATCH/DELETE užklausas be galiojančio X-CSRF-Token.

export const API_BASE = process.env.REACT_APP_API_URL || '';

const TOKEN_KEY = 'auth_token';
const CSRF_KEY = 'csrf_token';
const UNSAFE = new Set(['post', 'put', 'patch', 'delete']);

const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch {} },
};

export const getAuthToken = () => store.get(TOKEN_KEY);
export const getCsrfToken = () => store.get(CSRF_KEY);

export function setSession({ token, csrf } = {}) {
  if (token !== undefined) store.set(TOKEN_KEY, token);
  if (csrf !== undefined) store.set(CSRF_KEY, csrf);
}

export function clearSession() {
  store.set(TOKEN_KEY, null);
  store.set(CSRF_KEY, null);
}

let csrfPromise = null;

// Gauna (ir išsaugo) CSRF žetoną. Jei sesija nebegalioja – išvalo seną žetoną.
export function refreshCsrf() {
  if (!csrfPromise) {
    const token = getAuthToken();
    csrfPromise = axios
      .get(`${API_BASE}/auth/csrf`, { headers: token ? { 'X-Auth-Token': token } : {}, timeout: 8000 })
      .then((r) => {
        if (token && !r.data.authenticated) store.set(TOKEN_KEY, null);
        store.set(CSRF_KEY, r.data.csrf_token);
        return r.data.csrf_token;
      })
      .finally(() => { csrfPromise = null; });
  }
  return csrfPromise;
}

export async function ensureCsrf() {
  return getCsrfToken() || refreshCsrf();
}

// Antraštės rankiniams fetch() kvietimams
export async function authHeaders(extra = {}) {
  const headers = { ...extra, 'X-CSRF-Token': await ensureCsrf() };
  const token = getAuthToken();
  if (token) headers['X-Auth-Token'] = token;
  return headers;
}

const isCsrfError = (res) => res?.status === 403 && res?.data?.code === 'csrf_invalid';

// fetch() su X-Auth-Token / X-CSRF-Token ir vienu pakartojimu, jei CSRF žetonas pasenęs
export async function secureFetch(path, options = {}) {
  const doFetch = async () => fetch(`${API_BASE}${path}`, {
    ...options,
    headers: await authHeaders(options.headers || {}),
  });
  let res = await doFetch();
  if (res.status === 403) {
    const body = await res.clone().json().catch(() => null);
    if (isCsrfError({ status: 403, data: body })) {
      await refreshCsrf();
      res = await doFetch();
    }
  }
  return res;
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 8000,
});

api.interceptors.request.use(async (cfg) => {
  // X-CSRF-Token siunčiamas su visomis užklausomis; keičiančioms – privalomas (backend tikrina)
  const method = (cfg.method || 'get').toLowerCase();
  let csrf = getCsrfToken();
  if (!csrf) {
    try { csrf = await ensureCsrf(); } catch (e) { if (UNSAFE.has(method)) throw e; }
  }
  const token = getAuthToken();
  if (token) cfg.headers['X-Auth-Token'] = token;
  if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config;
    if (cfg && !cfg._csrfRetried && isCsrfError(err.response)) {
      cfg._csrfRetried = true;
      await refreshCsrf();
      return api(cfg);
    }
    // backend trumpam nepasiekiamas (pvz. perkraunamas) – GET bandome dar 2 kartus
    const status = err.response?.status;
    const transient = !err.response || [502, 503, 504].includes(status);
    if (cfg && (cfg.method || 'get').toLowerCase() === 'get' && transient && (cfg._retries || 0) < 2) {
      cfg._retries = (cfg._retries || 0) + 1;
      await new Promise((r) => setTimeout(r, 1500 * cfg._retries));
      return api(cfg);
    }
    return Promise.reject(err);
  },
);

export default api;
